import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import SocialShare from '../components/SocialShare';
import SEO from '../components/SEO';
import HilltopBanner from '../components/HilltopBanner';


function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await apiService.getArticle(slug);
        if (response.success) {
          setArticle(response.data);
          //console.log(response.data);
          //console.log('ad_scripts:', article.ad_scripts);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto px-6 py-32 text-center">
        <div className="glass p-12 rounded-2xl max-w-xl mx-auto border-red-500/30">
          <span className="material-symbols-outlined text-6xl text-red-500 mb-6">error</span>
          <h1 className="text-3xl font-bold text-white mb-4 uppercase">Node_Not_Found</h1>
          <p className="text-slate-400 mb-8">The requested intelligence unit does not exist in the current registry.</p>
          <Link to="/" className="bg-brand text-[#050805] px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform glow-green">
            REVERT_TO_ROOT
          </Link>
        </div>
      </div>
    );
  }

  const keywordsArray = article.keywords.split(",").map(keyword => keyword.trim());
  const keywordsString = keywordsArray.join(', ');

  return (
    <div className="min-h-screen py-12 px-6 md:px-12">
      <SEO
        title={article.title}
        description={article.meta_description}
        keywords={keywordsString}
        toolType="blog"
      />

      <div className="container mx-auto max-w-4xl">
        <article className="glass rounded-[32px] overflow-hidden">
          {/* Header Section */}
          <div className="relative p-8 md:p-12 border-b border-white/5 bg-gradient-to-b from-brand/5 to-transparent">
            <div className="flex items-center gap-4 mb-8">
              <span className="label-caps text-xs text-brand">Intelligence_Report</span>
              {article.calculator_name && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/30 text-brand font-bold text-[10px] tracking-widest uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse"></span>
                  COMPUTE_ENABLED
                </div>
              )}
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              {article.title}
            </h1>

            <p className="text-slate-400 text-lg mb-8 leading-relaxed">
              {article.meta_description}
            </p>

            <div className="flex flex-wrap items-center gap-6 pt-4">
              <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 tracking-widest uppercase">
                <span className="material-symbols-outlined text-xs">calendar_today</span>
                PUBLISHED: {new Date(article.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>

              {keywordsArray.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  {keywordsArray.map(keyword => (
                    <span key={keyword} className="px-3 py-1 rounded-full bg-brand/10 border border-brand/20 text-brand text-xs font-medium hover:bg-brand/20 hover:border-brand/40 transition-all cursor-default">
                      {keyword}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="absolute top-0 right-0 w-64 h-64 bg-brand/5 blur-3xl pointer-events-none -z-10"></div>
          </div>

          {/* Content Section */}
          <div className="p-8 md:p-12">
            <div
              className="prose prose-invert lg:prose-xl max-w-none mb-16 prose-headings:text-white prose-p:text-slate-400 prose-strong:text-brand"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Calculator CTA Box */}
            {article.calculator_name && (
              <div className="glass rounded-2xl border-brand/30 p-8 relative overflow-hidden group">
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-brand rounded-xl flex items-center justify-center text-[#050805] shadow-[0_0_20px_rgba(0,255,136,0.4)]">
                      <span className="material-symbols-outlined">calculate</span>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-brand uppercase tracking-widest">Execute_Protocol</span>
                      <h3 className="text-2xl font-bold text-white">{article.calculator_name}</h3>
                    </div>
                  </div>

                  <p className="text-slate-400 mb-8 max-w-xl">
                    Our high-performance compute engine is available for immediate execution based on the parameters discussed in this report.
                  </p>

                  <div className="flex flex-wrap gap-4">
                    <Link
                      to={`/calculators/${article.calculator_slug}`}
                      className="bg-brand text-[#050805] px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform glow-green"
                    >
                      LAUNCH CALCULATOR
                    </Link>
                    <Link
                      to="/calculators"
                      className="bg-transparent text-brand border border-brand/30 px-8 py-3 rounded-full font-bold hover:bg-brand/5 transition-colors"
                    >
                      Browse Calculators
                    </Link>
                  </div>
                </div>

                {/* Decorative Pattern */}
                <div className="absolute top-0 right-0 w-48 h-full bg-grid-pattern opacity-10 pointer-events-none"></div>
              </div>
            )}

            <div className="flex justify-center mt-16 border-t border-white/5 pt-16">
              <HilltopBanner />
            </div>
          </div>

          {/* Social & Footer Section */}
          <div className="p-8 md:p-12 bg-white/[0.02] border-t border-white/5">
            <div className="mb-12">
              <SocialShare
                url={window.location.href}
                title={article.title}
                description={article.meta_description}
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
              <Link to="/" className="text-brand font-bold text-[10px] tracking-[0.2em] flex items-center gap-2 group uppercase">
                <span className="material-symbols-outlined text-sm group-hover:-translate-x-1 transition-transform">arrow_back</span>
                REVERT_TO_ROOT
              </Link>
              <Link to="/calculators" className="bg-white/5 border border-white/10 text-slate-400 px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-brand/10 hover:text-brand hover:border-brand/20 transition-all">
                EXPLORE_REGISTRY
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

export default ArticlePage;